import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormActionBar, WindowContent, toast } from '@/shared/ui';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { getGetProductsPlaceholderQueryKey } from '@/api/generated/products-placeholders/products-placeholders';
import { PLACEHOLDER_LIST_KEY } from '../internal/schemesQueryKeys';
import { usePlaceholderForm } from './usePlaceholderForm';
import { PlaceholderTypePanel } from './components/PlaceholderTypePanel';
import { PlaceholderIdentityPanel } from './components/PlaceholderIdentityPanel';
import { PlaceholderTypeSpecificPanel } from './components/PlaceholderTypeSpecificPanel';
import type { PlaceholderFormValues } from './placeholderFormSchema';

export interface PlaceholderDetailViewProps {
  layerId: string;
  placeholderId?: string;
  initialValues: PlaceholderFormValues;
}

/**
 * Orchestrátor detailu placeholderu — drží formulář (`usePlaceholderForm`), skládá tři panely
 * (typ + náklad, identita, parametry dle typu) a sdílenou lištu akcí v patičce.
 */
export function PlaceholderDetailView({
  layerId,
  placeholderId,
  initialValues,
}: PlaceholderDetailViewProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const isEdit = placeholderId != null;

  const close = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form, submit } = usePlaceholderForm({
    placeholderId,
    initialValues,
    onSaved: (id) => {
      void queryClient.invalidateQueries({ queryKey: getGetProductsPlaceholderQueryKey(id) });
      void queryClient.invalidateQueries({ queryKey: [PLACEHOLDER_LIST_KEY] });
      toast({ severity: 'success', description: t('schemes.placeholder.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('schemes.placeholder.toast.errorTitle'),
        description: t('schemes.placeholder.toast.saveError'),
      });
    },
  });

  const title = isEdit ? t('schemes.placeholder.titleEdit') : t('schemes.placeholder.titleCreate');

  return (
    <WindowContent
      as="form"
      width="reading"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      footer={
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ canSubmit, isSubmitting }) => (
            <FormActionBar
              onBack={close}
              onSave={submit}
              saving={isSubmitting}
              disabled={!canSubmit}
            />
          )}
        </form.Subscribe>
      }
    >
      <div className="text-phx-md text-phx-text font-semibold">{title}</div>
      <PlaceholderTypePanel form={form} />
      <PlaceholderIdentityPanel form={form} />
      <PlaceholderTypeSpecificPanel form={form} />
    </WindowContent>
  );
}
